export interface IPeriodInput extends IPeriodCommon {
  id?: string;
  startDate?: string;
}

export interface IPeriodCommon {
  endDate?: string;
}

export interface IPeriod extends IPeriodCommon {
  startDate: string;
  id?: string;
}

export class Location {
  id?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  region?: string;
  longitude?: number;
  latitude?: number;
  range?: number;
}

export class ISkill implements ILabels {
  id!: string;
  label!: string;
  iso!: boolean;
  alternateLabels!: string[];
}

export class BackgroundTalent {
  id!: string;
  label?: string;
  description?: string;
  url?: string;
  type?: BackgroundType;
  name!: string;
}

export class SkillTalent {
  id?: string;
  current?: boolean;
  skill!: ISkill;
  period?: IPeriod;
  level?: number;
  background?: BackgroundTalent;
}

export class JobTalent {
  id?: string;
  jobTitle?: { id?: string; label: string; iso?: boolean }[];
  period?: IPeriod;
  current?: boolean;
  label?: string;
  description?: string;
  url?: string;
  type?: BackgroundType;
  name?: string;
  talentId?: string;
  skills?: ILabels[];
}

export class SkillTalentLevel {
  id!: string;
  skill!: ISkill;
  level?: number;
}

export class EducationTitleCategory {
  id!: string;
  label!: string;
}

export class EducationTitle {
  id!: string;
  iso!: boolean;
  alternateLabels!: string[];
  label!: string;
  category?: EducationTitleCategory;
}

export class EducationTalent {
  id?: string;
  type?: BackgroundType;
  name!: string;
  label?: string;
  description?: string;
  url?: string;
  current?: boolean;
  talentId?: string;
  period?: IPeriod;
}

export class LanguagesTalent {
  id?: string;
  label!: string;
  level?: number;
}

export enum RemoteWork {
  Flexible = 'Flexible',
  Remote = 'Remote',
  OnSite = 'OnSite',
}

export enum DesiredContractType {
  CDI = 'CDI',
  Freelance = 'Freelance',
  FreeAndCDI = 'FreeAndCDI',
}

export enum BloomerStatus {
  Bloomer = 'Bloomer',
  SuperBloomer = 'SuperBloomer',
}

export enum BackgroundType {
  Job = 'Job',
  Education = 'Education',
}

export interface ILabels {
  id: string;
  label: string;
  alternateLabels: string[];
  iso: boolean;
}

export class JobTitleCategory {
  id!: string;
  label!: string;
}

export class IJobTitle implements ILabels {
  id!: string;
  label!: string;
  iso!: boolean;
  alternateLabels!: string[];
  category?: JobTitleCategory;
}

export enum ProfileNetwork {
  LinkedIn = 'LinkedIn',
  ReseauPerso = 'Réseau Perso',
  TurnoverIT = 'TurnoverIT',
  Google = 'Google',
  Pylote = 'Pylote',
  HelloWork = 'HelloWork',
  SiteBloomays = 'SiteBloomays',
  TheGlue = 'TheGlue',
  Recommandation = 'Recommandation',
  Apec = 'Apec',
  Whatsapp = 'Whatsapp',
  Hiresweet = 'Hiresweet',
  Autre = 'Autre',
  Github = 'Github',
}

export type Profile = {
  id?: string;
  username?: string;
  network?: ProfileNetwork | string;
  url?: string;
  talentId?: string;
};

export interface ITalent {
  id?: string;
  fullname?: string;
  firstname: string;
  lastname: string;
  positionTitle?: { id?: string; jobTitle: IJobTitle; talentId?: string }; // Champs normalisé
  jobTitle: string; // Champs normalisé
  label?: string; // Titre Linkedin
  cv?: string;
  image?: string;
  email?: string;
  secondaryEmails?: string[];
  phone?: string;
  secondaryPhones?: string[];
  url?: string;
  secondaryUrls?: string[];
  summary?: string;
  status?: DesiredContractType;
  remoteWork?: RemoteWork;
  owner?: string;
  bloomerStatus?: BloomerStatus;
  tjm?: number;
  desiredTjm?: number;
  package?: number;
  desiredPackage?: number;
  packageVariable?: number;
  availabilityDate?: Date;
  location?: Location;
  profiles?: Profile[];
  locationId?: string;
  jobs?: JobTalent[];
  educations?: EducationTalent[];
  languages?: LanguagesTalent[];
  skills?: SkillTalent[];
  skillLevels?: SkillTalentLevel[];
  comment?: string;
  createdAt?: Date;
  updatedAt?: Date;
  origin: ProfileNetwork | string;
  hiresweetId?: string;
  airtableId?: string;
  collectiveId?: string;
  lastATSResumeRefresh?: Date | null;
}

/**
 * Champs du Talent pouvant être mis à jour via updateTalentById (identifiants externes ATS).
 * Exclut les relations (jobs, educations, skills, etc.) qui nécessitent une logique d'upsert dédiée.
 */
export type ITalentSimpleUpdate = Partial<Pick<ITalent, 'hiresweetId' | 'collectiveId'>>;

export type ItemSkills = {
  isToDelete?: boolean;
  id: string;
  skillId?: string;
  level?: number;
  label: string;
  selected?: boolean;
};

export interface ISkillInput {
  id?: string;
  label?: string;
  iso?: boolean;
  alternateLabels?: string[];
  level?: number;
}

export type DirectSkill = {
  id?: string;
  skill: ISkillInput;
  level?: number;
  talentId?: string;
};

export type ItemJobs = {
  id: string;
  level?: number;
  label: string;
  selected?: boolean;
};

export type ItemLanguages = {
  id: string;
  label: string;
  selected?: boolean;
  level?: number;
};

export type Language = {
  id: string;
  label: string;
  level?: number;
  talentId?: string;
};

export class JobTitleInput {
  id?: string;
  label?: string;
  iso?: boolean;
  alternateLabels?: string[];
}

export interface IJobInput {
  id?: string;
  current?: boolean;
  period?: IPeriodInput;
  label?: string;
  description?: string;
  url?: string;
  type?: BackgroundType;
  jobTitle?: JobTitleInput[];
  name: string;
  talentId?: string;
  skills?: ISkillInput[];
}

export interface ILocationCommon {
  address?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  region?: string;
  longitude?: number;
  latitude?: number;
  range?: number;
}

export interface ILocation extends ILocationCommon {
  id: string;
}

export interface ILocationInput extends ILocationCommon {
  id?: string;
  talentId?: string;
}

export interface IProfileCommon {
  username?: string;
  network?: ProfileNetwork | string;
  url?: string;
}

export interface IProfile extends IProfileCommon {
  id: string;
  talentId?: string;
}

export interface IProfileInput extends IProfileCommon {
  id?: string;
  talentId?: string;
}

export interface IBasics {
  name?: string;
  firstname: string;
  lastname: string;
  jobTitle: string;
  positionTitle?: { id?: string; talentId?: string; jobTitle: JobTitleInput };
  label?: string;
  image?: string;
  email?: string;
  secondaryEmails?: string[];
  phone?: string;
  secondaryPhones?: string[];
  url?: string;
  secondaryUrls?: string[];
  summary?: string;
  status?: DesiredContractType;
  remoteWork?: RemoteWork;
  owner?: string;
  bloomerStatus?: string;
  tjm?: number;
  desiredTjm?: number;
  package?: number;
  desiredPackage?: number;
  packageVariable?: number;
  availabilityDate?: string;
  availabilityDelay?: number;
  location?: ILocationInput;
  profiles?: IProfileInput[];
  cv?: string;
  origin?: ProfileNetwork | string;
  hiresweetId?: string;
  airtableId?: string;
  collectiveId?: string;
}

export interface IWorkInput {
  id?: string;
  periodId?: string;
  name?: string;
  label?: string;
  position?: string[];
  url?: string;
  summary?: string;
  startDate?: Date;
  endDate?: Date;
  highlights?: string[];
  skills?: ISkillInput[];
}

export interface ILanguageCommon {
  label: string;
  level?: number;
  talentId: string;
}

export interface ILanguage extends ILanguageCommon {
  id: string;
}

export interface ILanguageInput extends ILanguageCommon {
  id?: string;
  isToDelete?: boolean;
}

export interface ILanguageBasicsInput extends Omit<ILanguageInput, 'talentId'> {
  talentId?: string;
}

export interface IEducationBasics {
  id?: string;
  periodId?: string;
  institution?: string;
  url?: string;
  area: string;
  studyType?: string;
  courses?: string[];
  score?: string;
  beginDate?: Date;
  endDate?: Date;
}
export interface ITalentInput {
  id?: string;
  legacyObjectID?: string;
  basics?: IBasics;
  work?: IWorkInput[];
  education?: IEducationBasics[];
  skills?: ISkillInput[];
  languages?: ILanguageBasicsInput[];
  comment?: string;
}

export interface ITalentBasicInput {
  id?: string;
  legacyObjectID?: string;
  basics?: IBasics;
  comment?: string;
}

export enum EvenementType {
  createTalent = 'createTalent',
  updateTalent = 'updateTalent',
  createJobTalent = 'createJobTalent',
  updateJobTalent = 'updateJobTalent',
  deleteJobTalent = 'deleteJobTalent',
  createEducationTalent = 'createEducationTalent',
  updateEducationTalent = 'updateEducationTalent',
  deleteEducationTalent = 'deleteEducationTalent',
  createDirectSkill = 'createDirectSkill',
  updateDirectSkill = 'updateDirectSkill',
  deleteDirectSkill = 'deleteDirectSkill',
  createLang = 'createLang',
  updateLang = 'updateLang',
  deleteLang = 'deleteLang',
  createDirectJobTitle = 'createDirectJobTitle',
  updateDirectJobTitle = 'updateDirectJobTitle',
  deleteDirectJobTitle = 'deleteDirectJobTitle',
  createAttachmentTalent = 'createAttachmentTalent',
  updateAttachmentTalent = 'updateAttachmentTalent',
  deleteAttachmentTalent = 'deleteAttachmentTalent',
  createJobFunnelTalent = 'createJobFunnelTalent',
  moveJobFunnelTalent = 'moveJobFunnelTalent',
  addComment = 'addComment',
  normalizeSkill = 'normalizeSkill',
  normalizeJobTitle = 'normalizeJobTitle',
  phaseNotes = 'phaseNotes',
  synchroSkillTalentLevel = 'synchroSkillTalentLevel',
  tamCommentOnTalentProfile = 'tamCommentOnTalentProfile',
  clientCommentOnTalentProfile = 'clientCommentOnTalentProfile',
}

export type ActivityLog = {
  id: string;
  talent: string;
  type: EvenementType;
  source: string;
  tam?: string;
  data: string;
  previousData?: string;
  date: string;
  jobId: string | null;
  from: string | null;
  to: string | null;
  phaseTransitionDuration: number | null;
};

export interface TalentJobFunnel {
  jobId: string;
  talentId: string;
  addedInProject: Date | null;
  becameBloomer: boolean;
  topProfile: boolean;
  markedAsSeen: boolean;
  dateMarkedAsSeen: Date | null;
}

export type TalentJobFunnelUpsertData = Partial<TalentJobFunnel> & {
  jobId: string;
  talentId: string;
};
